import React, { useEffect, useState } from "react";
import { graphql, readInlineData } from "react-relay";

import type { FellowAttachment, FellowAttachmentRequest } from ".";
import { faFileTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { get } from "lodash";

import { useLazyLoadQuery } from "~/lib/relayUtil";

import { fileTypeIcon } from "./AttachmentDropzone";

import type { AttachmentPreviewAttachment$key } from "~/__generated__/AttachmentPreviewAttachment.graphql";
import type { AttachmentPreviewQuery } from "~/__generated__/AttachmentPreviewQuery.graphql";

import styles from "./css/attachments.module.css";

const MemoizedFontAwesomeIcon = React.memo(FontAwesomeIcon);

export const AttachmentPreviewAttachment = graphql`
	fragment AttachmentPreviewAttachment on Attachment @inline {
		id
		filename
		updatedAt
		directUrl: url
		size
		dimensions {
			width
			height
		}
		filetype
	}
`;

const attachmentPreviewQuery = graphql`
	query AttachmentPreviewQuery($id: ID!) {
		node(id: $id) {
			... on Attachment {
				...AttachmentPreviewAttachment
			}
		}
	}
`;

function FilePreview({
	url,
	type,
	id,
	className,
	onClick,
	innerClassName,
	onDragStart,
	onDragEnd,
}: {
	className: string | undefined;
	url: string;
	type: string;
	onDragStart: React.DragEventHandler<HTMLElement>;
	onDragEnd: React.DragEventHandler<HTMLElement>;
	id?: string;
	onClick: (event: React.MouseEvent<HTMLAnchorElement>, fileType: string) => void;
	innerClassName?: string;
	attachment?: FellowAttachment;
}) {
	const handleOnClick = React.useCallback(
		event => {
			onClick?.(event, type);
		},
		[onClick, type],
	);

	if (type.startsWith("image")) {
		return (
			<a
				href={url}
				onClick={handleOnClick}
				onDragStart={onDragStart}
				onDragEnd={onDragEnd}
				draggable={true}
				target="_blank"
				rel="noopener noreferrer"
				className={className}
			>
				<img
					src={url}
					alt="preview"
					className={cx(styles.attachmentPreviewInner, innerClassName)}
					data-attachment-id={id}
				/>
			</a>
		);
	}
	return (
		<a
			href={url}
			onClick={handleOnClick}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			draggable={true}
			target="_blank"
			rel="noopener noreferrer"
			className={cx(styles.attachmentPreviewFile, className)}
			data-attachment-id={id}
		>
			<MemoizedFontAwesomeIcon
				icon={fileTypeIcon(type)}
				className={cx(styles.attachmentPreviewInnerFile, innerClassName)}
				fixedWidth
				size="3x"
			/>
		</a>
	);
}

function PreviewFallback({
	className,
	localFile,
	onClick,
	innerClassName,
	onDragStart,
	onDragEnd,
}: {
	localFile: File | undefined;
	onDragStart: React.DragEventHandler<HTMLElement>;
	onDragEnd: React.DragEventHandler<HTMLElement>;
	onClick: (event: React.MouseEvent<HTMLAnchorElement>, fileType: string) => void;
	className: string | undefined;
	innerClassName?: string;
}) {
	const localURL = React.useMemo(() => {
		if (!localFile) return "";
		if (!localFile.type.startsWith("image")) return "";
		return URL.createObjectURL(localFile);
	}, [localFile]);

	React.useEffect(() => {
		const revoke = () => {
			if (localURL) {
				URL.revokeObjectURL(localURL);
			}
		};
		return revoke;
	}, [localURL]);

	if (!localFile) {
		return (
			<div className={styles.previewLoading}>
				<div className="ui text loader active" />
			</div>
		);
	}

	return (
		<FilePreview
			url={localURL}
			type={localFile.type}
			onClick={onClick}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			className={className}
			innerClassName={innerClassName}
		/>
	);
}

function AttachmentPreviewRelay({
	id,
	className,
	onClick,
	innerClassName,
	dimensionCollector,
	onDragStart,
	onDragEnd,
}: {
	id: string;
	className: string | undefined;
	onClick: (event: React.MouseEvent<HTMLAnchorElement>, fileType: string) => void;
	onDragStart: React.DragEventHandler<HTMLElement>;
	onDragEnd: React.DragEventHandler<HTMLElement>;
	innerClassName?: string;
	dimensionCollector?: (attachmentId: string, dimensions: FellowAttachment["dimensions"]) => void;
}) {
	const props = useLazyLoadQuery<AttachmentPreviewQuery>(
		attachmentPreviewQuery,
		{ id },
		{ fetchPolicy: "store-or-network" },
	);

	const attachment = readInlineData<AttachmentPreviewAttachment$key>(AttachmentPreviewAttachment, props.node);

	useEffect(() => {
		if (attachment?.dimensions) dimensionCollector?.(attachment.id, attachment.dimensions);
	}, [attachment, dimensionCollector]);

	if (!attachment?.directUrl) {
		return (
			<div className={styles.previewBroken}>
				<MemoizedFontAwesomeIcon icon={faFileTimes} className={className} fixedWidth size="3x" />
			</div>
		);
	}

	return (
		<FilePreview
			url={attachment.directUrl}
			type={attachment.filetype || ""}
			id={attachment.id}
			onClick={onClick}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			className={className}
			innerClassName={innerClassName}
		/>
	);
}

function AttachmentPreviewInner({
	id,
	className,
	localFile,
	onClick,
	requestAttachment,
	innerClassName,
	onDragStart,
	onDragEnd,
}: {
	id: string;
	className?: string;
	localFile?: File;
	requestAttachment: FellowAttachmentRequest;
	onClick: (event: React.MouseEvent<HTMLAnchorElement>, fileType: string) => void;
	innerClassName?: string;
	onDragStart: React.DragEventHandler<HTMLElement>;
	onDragEnd: React.DragEventHandler<HTMLElement>;
}) {
	const [loading, setLoading] = useState<boolean>(true);
	const [attachment, setAttachment] = useState<FellowAttachment | undefined>();
	useEffect(() => {
		const loadAttachment = async () => {
			setAttachment(await requestAttachment(id));
			setLoading(false);
		};
		loadAttachment();
	}, [id, requestAttachment]);
	if (loading) {
		return (
			<PreviewFallback
				localFile={localFile}
				onClick={onClick}
				onDragStart={onDragStart}
				onDragEnd={onDragEnd}
				className={className}
				innerClassName={innerClassName}
			/>
		);
	} else if (!attachment) {
		return (
			<div className={styles.previewBroken}>
				<MemoizedFontAwesomeIcon icon={faFileTimes} className={className} fixedWidth size="3x" />
			</div>
		);
	}
	return (
		<FilePreview
			url={get(attachment, "directUrl", get(attachment, "url"))}
			type={attachment.filetype}
			id={id}
			onClick={onClick}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			className={className}
			innerClassName={innerClassName}
			attachment={attachment}
		/>
	);
}

export default function AttachmentPreviewWrapper({
	id,
	className,
	localFile,
	onClick,
	requestAttachment,
	innerClassName,
	dimensionCollector,
	onDragStart,
	onDragEnd,
}: {
	id: string;
	onDragStart: React.DragEventHandler<HTMLElement>;
	onDragEnd: React.DragEventHandler<HTMLElement>;
	className?: string;
	localFile?: File;
	requestAttachment?: FellowAttachmentRequest;
	onClick: (event: React.MouseEvent<HTMLAnchorElement>, fileType: string) => void;
	innerClassName?: string;
	dimensionCollector?: (attachmentId: string, dimensions: FellowAttachment["dimensions"]) => void;
}) {
	// For the time being, this function supports use in a relay environment OR
	// in a non-relay environment if the "requestAttachment" callback is available
	if (!requestAttachment) {
		return (
			<React.Suspense
				fallback={
					<PreviewFallback
						localFile={localFile}
						onClick={onClick}
						onDragStart={onDragStart}
						onDragEnd={onDragEnd}
						className={className}
						innerClassName={innerClassName}
					/>
				}
			>
				<AttachmentPreviewRelay
					id={id}
					className={className}
					onClick={onClick}
					onDragStart={onDragStart}
					onDragEnd={onDragEnd}
					innerClassName={innerClassName}
					dimensionCollector={dimensionCollector}
				/>
			</React.Suspense>
		);
	}

	// We have a function to get attachments directly, so don't hit relay.
	return (
		<AttachmentPreviewInner
			id={id}
			className={className}
			localFile={localFile}
			requestAttachment={requestAttachment}
			onClick={onClick}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			innerClassName={innerClassName}
		/>
	);
}
